import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  Shield,
  Key,
  UserCheck,
  UserX,
  RefreshCw,
  Eye,
  EyeOff
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPermissions, setShowPermissions] = useState(null);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  
  // Create form state
  const [createForm, setCreateForm] = useState({
    username: "",
    password: "",
    display_name: "",
    role: "staff",
    permissions: []
  });

  const togglePasswordVisibility = (userId) => {
    setVisiblePasswords(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, modulesRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/auth/modules`)
      ]);
      setUsers(usersRes.data);
      setModules(modulesRes.data);
    } catch (error) {
      toast.error("Failed to load user data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!createForm.username || !createForm.password || !createForm.display_name) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await axios.post(`${API}/users`, createForm);
      toast.success(`User "${createForm.display_name}" created successfully`);
      setShowCreateForm(false);
      setCreateForm({ username: "", password: "", display_name: "", role: "staff", permissions: [] });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      await axios.put(`${API}/users/${userId}`, updates);
      toast.success("User updated successfully");
      setEditingUser(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const handleDeleteUser = async (userId, displayName) => {
    if (!window.confirm(`Are you sure you want to delete "${displayName}"? This cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("User deleted successfully");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/users/${userId}`, { is_active: !currentStatus });
      toast.success(`User ${currentStatus ? 'disabled' : 'enabled'} successfully`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user status");
    }
  };

  const handleUpdatePermissions = async (userId, permissions) => {
    try {
      await axios.put(`${API}/users/${userId}/permissions`, { user_id: userId, permissions });
      toast.success("Permissions updated successfully");
      setShowPermissions(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update permissions");
    }
  };

  const togglePermission = (permList, moduleId) => {
    if (permList.includes(moduleId)) {
      return permList.filter(p => p !== moduleId);
    } else {
      return [...permList, moduleId];
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-600" />
        <span className="ml-3 text-lg text-slate-600">Loading users...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="user-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 font-heading">User Management</h2>
          <p className="text-slate-600 mt-1">Manage users and their permissions</p>
        </div>
        <Button 
          onClick={() => setShowCreateForm(!showCreateForm)}
          data-testid="create-user-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create User
        </Button>
      </div>

      {/* Create User Form */}
      {showCreateForm && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader>
            <CardTitle className="text-lg">Create New User</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label>Username *</Label>
                <Input 
                  value={createForm.username}
                  onChange={(e) => setCreateForm({...createForm, username: e.target.value})}
                  placeholder="e.g., john.doe"
                  data-testid="create-username-input"
                />
              </div>
              <div>
                <Label>Password *</Label>
                <Input 
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({...createForm, password: e.target.value})}
                  placeholder="Min 6 characters"
                  data-testid="create-password-input"
                />
              </div>
              <div>
                <Label>Display Name *</Label>
                <Input 
                  value={createForm.display_name}
                  onChange={(e) => setCreateForm({...createForm, display_name: e.target.value})}
                  placeholder="e.g., John Doe"
                  data-testid="create-displayname-input"
                />
              </div>
              <div>
                <Label>Role</Label>
                <select
                  className="w-full h-10 px-3 border border-slate-200 rounded-md"
                  value={createForm.role}
                  onChange={(e) => setCreateForm({...createForm, role: e.target.value})}
                  data-testid="create-role-select"
                >
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            
            {/* Permissions */}
            <div>
              <Label className="mb-2 block">Module Permissions</Label>
              {(modules.groups || []).map((group) => (
                <div key={group.id} className="mb-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{group.name}</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {(modules.modules || []).filter(m => m.group === group.id).map((module) => (
                      <div key={module.id} className="flex items-center gap-2 p-2 bg-white rounded border">
                        <Checkbox 
                          id={`create-${module.id}`}
                          checked={createForm.permissions.includes(module.id)}
                          onCheckedChange={() => setCreateForm({
                            ...createForm, 
                            permissions: togglePermission(createForm.permissions, module.id)
                          })}
                        />
                        <label htmlFor={`create-${module.id}`} className="text-sm cursor-pointer">
                          {module.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreateForm(false)}>Cancel</Button>
              <Button onClick={handleCreateUser} data-testid="confirm-create-user-btn">
                Create User
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Users className="w-5 h-5" />
            Users ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 font-semibold text-slate-600">User</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-600">Username</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-600">Password</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-600">Role</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-600">Status</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-600">Permissions</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.user_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${user.role === 'admin' ? 'bg-purple-500' : 'bg-slate-500'}`}>
                          {user.display_name?.charAt(0)?.toUpperCase() || "U"}
                        </div>
                        <span className="font-medium">{user.display_name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 font-mono text-slate-600">{user.username}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-slate-600">
                          {visiblePasswords[user.user_id] 
                            ? (user.plain_password || "••••••••") 
                            : "••••••••"}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => togglePasswordVisibility(user.user_id)}
                          title={visiblePasswords[user.user_id] ? "Hide password" : "Show password"}
                        >
                          {visiblePasswords[user.user_id] 
                            ? <EyeOff className="w-4 h-4 text-slate-400" />
                            : <Eye className="w-4 h-4 text-slate-400" />
                          }
                        </Button>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant={user.role === "admin" ? "default" : "secondary"} className={user.role === "admin" ? "bg-purple-600" : ""}>
                        {user.role === "admin" && <Shield className="w-3 h-3 mr-1" />}
                        {user.role}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      <Badge 
                        variant={user.is_active ? "outline" : "destructive"}
                        className={user.is_active ? "bg-emerald-100 text-emerald-800" : ""}
                      >
                        {user.is_active ? (
                          <><UserCheck className="w-3 h-3 mr-1" />Active</>
                        ) : (
                          <><UserX className="w-3 h-3 mr-1" />Disabled</>
                        )}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1 flex-wrap max-w-xs">
                        {user.permissions?.includes("all") ? (
                          <Badge variant="secondary" className="bg-purple-100 text-purple-800">All Modules</Badge>
                        ) : user.permissions?.length > 0 ? (
                          <>
                            <Badge variant="outline">{user.permissions.length} modules</Badge>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => setShowPermissions(showPermissions === user.user_id ? null : user.user_id)}
                            >
                              <Key className="w-3 h-3" />
                            </Button>
                          </>
                        ) : (
                          <Badge variant="outline" className="text-slate-400">No access</Badge>
                        )}
                      </div>
                      
                      {/* Inline permissions editor */}
                      {showPermissions === user.user_id && (
                        <PermissionsEditor 
                          user={user}
                          modules={modules}
                          onSave={(perms) => handleUpdatePermissions(user.user_id, perms)}
                          onCancel={() => setShowPermissions(null)}
                        />
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleToggleActive(user.user_id, user.is_active)}
                          title={user.is_active ? "Disable user" : "Enable user"}
                        >
                          {user.is_active ? <UserX className="w-4 h-4 text-amber-500" /> : <UserCheck className="w-4 h-4 text-emerald-500" />}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteUser(user.user_id, user.display_name)}
                          title="Delete user"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan="7" className="py-8 text-center text-slate-500">
                      <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      No users found. Create one to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Permissions Editor Component
function PermissionsEditor({ user, modules, onSave, onCancel }) {
  const [permissions, setPermissions] = useState(user.permissions || []);
  const groups = modules.groups || [];
  const allModules = modules.modules || [];

  const togglePermission = (moduleId) => {
    if (permissions.includes(moduleId)) {
      setPermissions(permissions.filter(p => p !== moduleId));
    } else {
      setPermissions([...permissions, moduleId]);
    }
  };

  const toggleGroup = (group) => {
    const groupModuleIds = allModules.filter(m => m.group === group.id).map(m => m.id);
    const allChecked = groupModuleIds.every(id => permissions.includes(id));
    if (allChecked) {
      setPermissions(permissions.filter(p => !groupModuleIds.includes(p)));
    } else {
      setPermissions([...new Set([...permissions, ...groupModuleIds])]);
    }
  };

  return (
    <div className="mt-3 p-3 bg-slate-50 rounded-lg border">
      {groups.map((group) => {
        const groupMods = allModules.filter(m => m.group === group.id);
        const allChecked = groupMods.every(m => permissions.includes(m.id) || permissions.includes("all"));
        return (
          <div key={group.id} className="mb-3">
            <div className="flex items-center gap-2 mb-1">
              <Checkbox
                checked={allChecked}
                onCheckedChange={() => toggleGroup(group)}
                disabled={permissions.includes("all")}
              />
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{group.name}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 ml-6">
              {groupMods.map((module) => (
                <div key={module.id} className="flex items-center gap-2">
                  <Checkbox 
                    id={`perm-${user.user_id}-${module.id}`}
                    checked={permissions.includes(module.id) || permissions.includes("all")}
                    onCheckedChange={() => togglePermission(module.id)}
                    disabled={permissions.includes("all")}
                  />
                  <label htmlFor={`perm-${user.user_id}-${module.id}`} className="text-xs cursor-pointer">
                    {module.name}
                  </label>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
        <Button size="sm" onClick={() => onSave(permissions)}>Save</Button>
      </div>
    </div>
  );
}
